import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Mail01, Lock01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { accountService } from '@/data/mock-accounts';
import { useTranslation } from 'react-i18next';
import { EveLogo } from '@/components/foundations/logo/eve-logo';
import { VersionFooter } from '@/components/base/version-footer/version-footer';

export const LoginPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation(['pages:auth', 'common']);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            // Simulate login process
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Find account by email and password
            const accounts = accountService.getAll();
            const account = accounts.find(acc => acc.email === email && acc.password === password);

            if (!account) {
                setError(t('login.errorInvalidCredentials'));
                setIsLoading(false);
                return;
            }

            // Save user info in localStorage
            localStorage.setItem('userType', account.userType);
            localStorage.setItem('accountId', account.id);
            localStorage.setItem('selectedAccountId', account.id);
            localStorage.setItem('accountName', account.legalName);

            // Emit custom event per notificare il provider del cambio account
            window.dispatchEvent(new Event('accountIdChanged'));

            // Redirect based on user type
            if (account.userType === 'SHIPPER') {
                navigate('/shipper/dashboard');
            } else if (account.userType === 'RECEIVER') {
                navigate('/receiver/dashboard');
            } else if (account.userType === 'ADMIN') {
                navigate('/admin/dashboard');
            } else if (account.userType === 'FREIGHT_FORWARDER') {
                navigate('/freight-forwarder/dashboard');
            } else {
                // Fallback
                navigate('/admin/dashboard');
            }
        } catch (err) {
            setError(t('login.errorLogin'));
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-secondary px-4 py-12">
            <div className="w-full max-w-md">
                {/* Logo */}
                <Link to="/" className="flex justify-center items-center gap-2 mb-8 hover:opacity-80 transition-opacity">
                    <EveLogo className="h-8 w-8" />
                    <h2 className="text-2xl font-bold dark:text-white">Eve Mind</h2>
                </Link>

                {/* Login Form */}
                <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary mb-4">
                    <div className="mb-6 text-center">
                        <h1 className="text-2xl font-semibold text-primary mb-2">
                            {t('login.title')}
                        </h1>
                        <p className="text-sm text-tertiary">
                            {t('login.subtitle')}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <Input
                            type="email"
                            label={t('login.emailLabel')}
                            placeholder={t('login.emailPlaceholder')}
                            value={email}
                            onChange={setEmail}
                            icon={Mail01}
                            isRequired
                            size="md"
                        />

                        <Input
                            type="password"
                            label={t('login.passwordLabel')}
                            placeholder={t('login.passwordPlaceholder')}
                            value={password}
                            onChange={setPassword}
                            icon={Lock01}
                            isRequired
                            size="md"
                        />

                        <div className="flex items-center justify-between">
                            <Link
                                to="/auth/reset-password"
                                className="text-sm text-brand-secondary hover:text-brand-secondary_hover"
                            >
                                {t('login.forgotPassword')}
                            </Link>
                        </div>

                        {error && (
                            <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            size="lg"
                            color="primary"
                            isLoading={isLoading}
                            className="w-full"
                        >
                            {t('login.submitButton')}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-sm text-tertiary">
                            {t('login.noAccount')}{' '}
                            <Link
                                to="/auth/register"
                                className="text-brand-secondary hover:text-brand-secondary_hover"
                            >
                                {t('login.contactAdmin')}
                            </Link>
                        </p>
                    </div>
                </div>

                <VersionFooter />
            </div>
        </div>
    );
};
